import { TsDOM as q } from '@/scripts/core/TsDOM'
import { normalizeEmbroideryArtwork } from '@/scripts/product/embroidery-artwork-fit'
import {
	applyPhotoFibers,
	embroideryMaterial,
} from '@/scripts/product/embroidery-material'

type Foot = 'right' | 'left'

export function threadColorMatrix(color: string) {
	const hex = color.replace('#', '')
	const expanded =
		hex.length === 3 ? [...hex].map((part) => part + part).join('') : hex
	if (!/^[\da-f]{6}$/i.test(expanded)) return null
	const channels = [0, 2, 4].map(
		(offset) => parseInt(expanded.slice(offset, offset + 2), 16) / 255
	)
	return channels
		.flatMap((channel) => [0, 0, 0, 0, channel])
		.concat([0, 0, 0, 1, 0])
		.join(' ')
}

const svgNamespace = 'http://www.w3.org/2000/svg'
let nextFilter = 0
function photoContext(token: HTMLElement) {
	const group = token.closest<HTMLElement>(
		'[data-uc-personalization-preview-shoe]'
	)
	const preview = token.closest<HTMLElement>('[data-uc-embroidery-preview]')
	if (!group || !preview) return
	const image = q.os<HTMLImageElement>(
		'.personalization-page__preview-image',
		preview
	)
	if (!image) return
	const foot =
		group.dataset.ucPersonalizationPreviewShoe === 'left' ? 'left' : 'right'
	const number = (name: string) =>
		parseFloat(preview.style.getPropertyValue(`--preview-${foot}-${name}`))
	const width = number('width')
	const gap = (width * number('gap')) / 100
	const tokenWidth = (width - 2 * gap) / 3
	const index = Array.from(group.children).indexOf(token)
	const offset =
		(index - (group.children.length - 1) / 2) * (tokenWidth + gap)
	const rotation = number('rotation')
	const radians = (rotation * Math.PI) / 180
	const sourceWidth = Number(image.getAttribute('width'))
	const sourceHeight = Number(image.getAttribute('height'))
	return {
		image,
		mapping: {
			sourceWidth,
			sourceHeight,
			xPercent: number('x') + offset * Math.cos(radians),
			yPercent:
				number('y') +
				(offset * Math.sin(radians) * sourceWidth) / sourceHeight,
			widthPercent: tokenWidth,
			rotationDeg: rotation,
			reflectPhoto: foot === 'left',
		},
	}
}

export function renderEmbroideryText(
	token: HTMLElement,
	value: string,
	color: string
) {
	const signature = JSON.stringify([value, color])
	if (token.dataset.previewText === signature) return
	token.dataset.previewText = signature
	delete token.dataset.previewGlyph
	token.dataset.previewAssetState = 'loading'
	token.textContent = value
	void document.fonts.ready.then(() => {
		if (token.dataset.previewText !== signature || !token.isConnected)
			return
		const canvas = document.createElement('canvas')
		canvas.width = canvas.height = 512
		const context = canvas.getContext('2d')
		if (!context) return
		const family =
			getComputedStyle(document.body).getPropertyValue(
				'--f-body-family'
			) || getComputedStyle(document.body).fontFamily
		context.font = `700 350px ${family}`
		context.textAlign = 'center'
		const originalMetrics = context.measureText(value)
		const artworkHeight =
			originalMetrics.actualBoundingBoxAscent +
			originalMetrics.actualBoundingBoxDescent
		const artworkWidth =
			originalMetrics.actualBoundingBoxLeft +
			originalMetrics.actualBoundingBoxRight
		const scale = Math.min(
			300 / Math.max(artworkHeight, 1),
			480 / Math.max(artworkWidth, 1)
		)
		context.font = `700 ${350 * scale}px ${family}`
		const metrics = context.measureText(value)
		const baseline =
			(512 +
				metrics.actualBoundingBoxAscent -
				metrics.actualBoundingBoxDescent) /
			2
		context.fillStyle = '#ffffff'
		context.fillText(value, 256, baseline)
		context.globalCompositeOperation = 'source-atop'
		for (let y = 0; y < 512; y += 5) {
			context.beginPath()
			context.moveTo(0, y)
			context.quadraticCurveTo(256, y + 18, 512, y - 4)
			context.strokeStyle = y % 10 === 0 ? '#d4d4d4' : '#e8e8e8'
			context.lineWidth = 1.5
			context.stroke()
		}
		renderEmbroideryGlyph(token, canvas.toDataURL('image/png'), color)
	})
}

export function renderEmbroideryGlyph(
	token: HTMLElement,
	url: string,
	color: string,
	originalColors = false,
	normalizeArtwork = false
) {
	const signature = JSON.stringify([
		url,
		color,
		originalColors,
		normalizeArtwork,
	])
	if (token.dataset.previewGlyph === signature) return
	token.dataset.previewGlyph = signature
	token.classList.add('has-stitch-art')
	token.dataset.previewAssetState = 'loading'
	const svg = document.createElementNS(svgNamespace, 'svg')
	svg.setAttribute('viewBox', '0 0 512 512')
	svg.setAttribute('aria-hidden', 'true')
	svg.classList.add('embroidery-preview__glyph')
	const image = document.createElementNS(svgNamespace, 'image')
	image.setAttribute('width', '512')
	image.setAttribute('height', '512')
	const matrix = originalColors ? null : threadColorMatrix(color)
	if (matrix) {
		const filter = document.createElementNS(svgNamespace, 'filter')
		const filterId = `EmbroideryThread-${++nextFilter}`
		filter.id = filterId
		filter.setAttribute('color-interpolation-filters', 'sRGB')
		const transfer = document.createElementNS(svgNamespace, 'feColorMatrix')
		transfer.setAttribute('type', 'matrix')
		transfer.setAttribute('values', matrix)
		filter.append(transfer)
		svg.append(filter)
		image.setAttribute('filter', `url(#${filterId})`)
	}
	const settle = (state: 'loaded' | 'error') => {
		if (!token.isConnected || token.dataset.previewGlyph !== signature)
			return
		if (state === 'error') image.remove()
		token.dataset.previewAssetState = state
		const preview = token.closest<HTMLElement>(
			'[data-uc-embroidery-preview]'
		)
		if (preview) updateAssetStatus(preview)
	}
	image.addEventListener('load', () => settle('loaded'), { once: true })
	image.addEventListener('error', () => settle('error'), { once: true })
	image.setAttribute('href', url)
	svg.append(image)
	token.replaceChildren(svg)
	void (async () => {
		// The token is appended by its caller before these photo coordinates are read.
		await Promise.resolve()
		if (!token.isConnected || token.dataset.previewGlyph !== signature)
			return
		const photo = photoContext(token)
		try {
			const source = normalizeArtwork
				? await normalizeEmbroideryArtwork(url)
				: url
			const material = await embroideryMaterial(
				source,
				color,
				originalColors,
				photo?.mapping.rotationDeg ?? 0
			)
			if (!token.isConnected || token.dataset.previewGlyph !== signature)
				return
			let result = material
			let treatment = 'thread'
			if (photo) {
				try {
					await photo.image.decode()
					if (
						!token.isConnected ||
						token.dataset.previewGlyph !== signature
					)
						return
					result = await applyPhotoFibers(
						material,
						photo.image.currentSrc || photo.image.src,
						photo.mapping
					)
					treatment = 'photo-fibers'
				} catch {
					/* A cross-origin photo must not hide the supplied stitch artwork. */
				}
			}
			if (!token.isConnected || token.dataset.previewGlyph !== signature)
				return
			image.removeAttribute('filter')
			image.setAttribute('href', result)
			token.dataset.previewMaterial = treatment
		} catch {
			if (token.dataset.previewGlyph === signature)
				token.dataset.previewMaterial = 'source'
		}
	})()
}

function updateAssetStatus(preview: HTMLElement) {
	const active = q.os<HTMLElement>(
		'[data-uc-personalization-preview-shoe]:not([hidden])',
		preview
	)
	const loading = Boolean(
		active?.querySelector('[data-preview-asset-state="loading"]')
	)
	const failed = Boolean(
		active?.querySelector('[data-preview-asset-state="error"]')
	)
	preview.setAttribute('aria-busy', String(loading))
	const status = q.os<HTMLElement>('[data-uc-preview-status]', preview)
	if (status) {
		status.textContent = failed
			? (preview.dataset.error ?? '')
			: loading
				? (preview.dataset.loading ?? '')
				: ''
		status.classList.toggle('visually-hidden', !failed)
	}
}

export function updateEmbroideryPreview(
	root: HTMLElement,
	foot: Foot,
	showFootControls: boolean
) {
	const preview = q.os<HTMLElement>('[data-uc-embroidery-preview]', root)
	if (!preview) return
	preview.dataset.foot = foot
	const controls = q.os<HTMLElement>('.embroidery-preview__controls', preview)
	if (controls) controls.hidden = !showFootControls
	const footControls = q.os<HTMLElement>('[data-uc-preview-feet]', preview)
	if (footControls) footControls.hidden = !showFootControls
	for (const button of q.ol<HTMLButtonElement>(
		'[data-uc-personalization-inspect-foot]',
		preview
	) ?? [])
		button.setAttribute(
			'aria-pressed',
			String(button.dataset.ucPersonalizationInspectFoot === foot)
		)
	updateAssetStatus(preview)
}
